import { useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { films } from "@/mocks/films";
import { useSeo } from "@/hooks/useSeo";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";
import FilmSection from "./components/FilmSection";
import NewsPreview from "./components/NewsPreview";

export default function Home() {
  useSeo({
    title: "久久爱-热门影视剧推荐与精彩内容集合",
    description:
      "久久爱提供丰富的电影、电视剧资源，涵盖最新热门影视剧和经典作品，满足用户多样化观看需求。用户可以浏览影视资讯、剧情介绍及评价。",
    keywords:
      "久久爱,影视剧,电影推荐,电视剧,影视资讯,剧情介绍,在线电影,电视剧观看",
  });

  const featured = useMemo(
    () => [...films].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    []
  );

  const latest = useMemo(
    () => [...films].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate)).slice(0, 8),
    []
  );

  const hotRanking = useMemo(
    () => [...films].sort((a, b) => b.popularity - a.popularity).slice(0, 6),
    []
  );

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <HeroCarousel items={featured} />

        <div className="-mt-8 md:-mt-10 relative z-20">
          <CategoryShortcuts />
        </div>

        <div className="mt-14 md:mt-20">
          <FilmSection
            title="最新影视作品"
            subtitle="按上映时间排序，追踪当下最值得关注的新作"
            films={latest}
            moreHref="/category"
          />
        </div>

        <section className="mx-auto mt-14 w-full px-4 md:mt-20 md:px-6">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950 md:text-2xl font-heading">
                <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
                热门影视推荐
              </h2>
              <p className="mt-1.5 text-xs text-foreground-500 md:text-sm">
                根据热度与编辑精选综合排序，这些作品最近被讨论得最多
              </p>
            </div>
            <Link
              to="/category"
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200 md:text-sm"
            >
              更多作品
              <i className="ri-arrow-right-s-line text-base"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-5">
            {hotRanking.map((film, index) => (
              <Link
                key={film.id}
                to={`/detail/${film.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-primary-200 hover:bg-background-100"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-sm font-extrabold text-background-50 font-heading">
                  {index + 1}
                </span>
                <span className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={film.cover}
                    alt={`${film.title}封面`}
                    title={`${film.title} - 久久爱热门影视`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-foreground-950 md:text-base">
                      {film.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-accent-600">
                      <i className="ri-star-fill"></i>
                      {film.rating.toFixed(1)}
                    </span>
                  </span>
                  <span className="mt-1 block truncate text-xs text-foreground-500">
                    {film.category} · {film.year} · {film.region} · {film.genres.join(" / ")}
                  </span>
                  <span className="mt-1.5 line-clamp-1 text-xs text-foreground-600">
                    主演：{film.actors.join("、")}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-14 md:mt-20">
          <NewsPreview />
        </div>

        <section className="mx-auto mt-14 w-full px-4 md:mt-20 md:px-6">
          <div className="overflow-hidden rounded-2xl bg-primary-900 p-6 md:p-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="text-xl font-extrabold text-background-50 md:text-3xl font-heading">
                  关于久久爱
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-primary-100 md:text-base">
                  久久爱是一个专注影视内容推荐与资讯的平台。我们相信，好的作品值得被认真介绍。这里既有最新上映的热门影视剧，也有经得起时间检验的经典之作。
                </p>
                <p className="mt-3 text-sm leading-relaxed text-primary-100 md:text-base">
                  你可以通过多维筛选快速找到想看的作品，浏览详细的剧情简介与演员信息，也可以在资讯板块读到深度的影评与行业观察。
                </p>
                <Link
                  to="/about"
                  className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-3 md:gap-5">
                {[
                  { value: "14+", label: "收录作品" },
                  { value: "4", label: "影视分类" },
                  { value: "6", label: "资讯专题" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg bg-primary-800 p-4 text-center md:p-5"
                  >
                    <p className="text-2xl font-extrabold text-accent-400 md:text-3xl font-heading">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-primary-200">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}